import classNames from 'classnames';
import { FiExternalLink } from 'react-icons/fi';
import { BsThreeDotsVertical } from 'react-icons/bs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { RouteComponentProps } from 'react-router-dom';

import history from 'utils/history';
import * as toast from 'utils/toast';
import { getIndexOfSN } from 'utils/table';
import { getFormattedDate } from 'utils/date';
import { handleError } from 'utils/errorHandler';
import { parse, stringify } from 'utils/queryParam';
import { interpolate, pluralize, withoutProtocolAndSubDomainURL } from 'utils/string';

import * as routes from 'constants/routes';
import * as permissions from 'constants/permissions';
import { DISPLAY_DATE_FORMAT } from 'constants/date';
import { MINIMUM_PAGE_COUNT, MINIMUM_PAGE_ITEM } from 'constants/page';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  EVENTS_REFERENCE_PERIOD,
  EVENTS_REFERENCE_PERIOD_PILL_TABS,
} from 'constants/appConstants';
import { EVENT_DESCRIPTION_LENGTH, EVENT_NAME_LENGTH, TOOLTIP_LIMIT_LENGTH } from 'constants/string';

import Table from 'components/common/table';
import PopOver from 'components/common/popover';
import PillTab from 'components/common/pillTab';
import Pagination from 'components/common/paginator';
import EditDelete from 'components/common/editDelete';
import TableHeader from 'components/common/table/TableHeader';

import * as eventService from 'services/events';
import { useUserPermissions } from 'hooks/query/useUserInfo';
import getCurrentReferencePeriod from 'utils/getCurrentReferencePeriod';
import useTitle from 'hooks/useTitle';

type PermissionMap = Record<string, boolean>;

type EventQueryState = {
  referencePeriod: string;
  page?: number;
  size?: number;
};

type EventMeta = {
  page: number;
  total: number;
};

type EventRecord = {
  id: number;
  name: string;
  description: string;
  link: string;
  date: string;
};

type EventRow = {
  original: EventRecord;
  index: number;
};

type PopperData = {
  styles: Record<string, string | number>;
};

type QueryValue = string | string[] | Record<string, unknown> | Array<string | Record<string, unknown>> | undefined;

const getNumberQueryValue = (value: QueryValue, fallback: number): number => {
  if (typeof value !== 'string') {
    return fallback;
  }

  const parsedValue = Number(value);
  return Number.isNaN(parsedValue) ? fallback : parsedValue;
};

const EventList: React.FC<RouteComponentProps> = (props) => {
  useTitle('Events');

  const [pageData, setPageData] = useState<EventMeta>({ page: DEFAULT_PAGE_NUMBER, total: 0 });
  const [pageCount, setPageCount] = useState(DEFAULT_PAGE_NUMBER);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const userPermissions = useUserPermissions() as PermissionMap;
  const [eventList, setEventList] = useState<EventRecord[]>([]);
  const [columnToSortBy, setColumnToSortBy] = useState('date');
  const [isAscendingOrdered, setIsAscendingOrdered] = useState(true);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [openPopoverId, setOpenPopoverId] = useState<number | null>(null);

  const currentReferencePeriod = getCurrentReferencePeriod(props);
  const initialReferencePeriod =
    typeof currentReferencePeriod === 'string'
      ? currentReferencePeriod
      : EVENTS_REFERENCE_PERIOD.UPCOMING.toLowerCase();

  const [queries, setQueries] = useState<EventQueryState>({
    referencePeriod: initialReferencePeriod,
  });

  const queryParams = parse(props.location.search, {
    decoder: (str) => decodeURIComponent(str),
  });

  const normalizedPage = getNumberQueryValue(queryParams.page, DEFAULT_PAGE_NUMBER);
  const normalizedSize = getNumberQueryValue(queryParams.size, DEFAULT_PAGE_SIZE);

  const { page, size, referencePeriod } = queries;

  const handleEdit = (row: EventRow) => {
    history.push(interpolate(routes.UPDATE_EVENT, { id: row.original.id }));
  };

  const createPopperOptions = (customStyles: Record<string, string | number>) => {
    return {
      distance: 5,
      popperOptions: {
        modifiers: {
          flip: {
            enabled: false,
          },
          addZIndex: {
            enabled: true,
            order: 812,
            fn: (data: PopperData) => {
              return {
                ...data,
                styles: {
                  ...data.styles,
                  ...customStyles,
                },
              };
            },
          },
        },
      },
    };
  };

  const fetchEvents = useCallback(async () => {
    setIsDataLoading(true);

    try {
      const filters = {
        ...queries,
        page: normalizedPage,
        size: normalizedSize,
      };

      setPageSize(normalizedSize);

      const { data, meta } = (await eventService.fetchEvents(filters)) as {
        data: Array<Omit<EventRecord, 'date'> & { date?: string; eventDate?: string }>;
        meta: EventMeta;
      };

      setPageData(meta);
      setPageCount(Math.ceil(meta.total / normalizedSize));

      const finalData = data.map((item) => {
        const dateSource = item.date || item.eventDate || '';
        const date = getFormattedDate(dateSource, DISPLAY_DATE_FORMAT);
        const description = item.description ? item.description : '-';
        const link = item.link ? item.link : '-';

        return {
          ...item,
          description,
          date,
          link,
        };
      });

      setEventList(finalData);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDataLoading(false);
    }
  }, [normalizedPage, normalizedSize, queries]);

  const handleDelete = async (row: EventRow) => {
    try {
      await eventService.removeEvent(row.original.id);
      toast.success({
        title: 'Success',
        message: 'Event deleted successfully.',
      });

      if (eventList.length === MINIMUM_PAGE_ITEM) {
        updateUrl({
          ...queries,
          page: DEFAULT_PAGE_NUMBER,
        });
      }

      fetchEvents();
    } catch (err) {
      handleError(err);
    }
  };

  useEffect(() => {
    const loadEvents = async () => {
      setIsDataLoading(true);

      try {
        const filters = {
          ...queries,
          page: normalizedPage,
          size: normalizedSize,
        };

        setPageSize(normalizedSize);

        const { data, meta } = (await eventService.fetchEvents(filters)) as {
          data: Array<Omit<EventRecord, 'date'> & { date?: string; eventDate?: string }>;
          meta: EventMeta;
        };

        setPageData(meta);
        setPageCount(Math.ceil(meta.total / normalizedSize));

        const finalData = data.map((item) => {
          const dateSource = item.date || item.eventDate || '';
          const date = getFormattedDate(dateSource, DISPLAY_DATE_FORMAT);
          const description = item.description ? item.description : '-';
          const link = item.link ? item.link : '-';

          return {
            ...item,
            description,
            date,
            link,
          };
        });

        setEventList(finalData);
      } catch (error) {
        handleError(error);
      } finally {
        setIsDataLoading(false);
      }
    };

    void loadEvents();
  }, [normalizedPage, normalizedSize, queries]);

  const getRowProps = (_state: object, rowInfo: EventRow) => {
    return {
      className: 'align-items-center d-flex',
      onClick: (e: React.MouseEvent) => {
        if (!userPermissions[permissions.EVENTS.UPDATE]) {
          return;
        }

        const url = interpolate(routes.UPDATE_EVENT, { id: rowInfo.original.id });

        if (e.ctrlKey || e.metaKey) {
          window.open(url);
        } else {
          history.push(url);
        }
      },
    };
  };

  const columns = useMemo(
    () => [
      {
        Header: <TableHeader label="SN." identifier="sn" />,
        accessor: 'sn',
        maxWidth: 56,
        sortable: false,
        filterable: false,
        Cell: (cell: EventRow) => {
          const index = getIndexOfSN(cell, pageSize, pageData);
          return <span className="capital-text employees__table-text">{index < 10 ? `0${index}` : index}</span>;
        },
      },
      {
        Header: (
          <TableHeader
            label="Title"
            identifier="name"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'name') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('name');
            }}
          />
        ),
        accessor: 'name',
        maxWidth: 340,
        Cell: (cell: EventRow) => {
          const isNotFirstRow = cell.index !== 0;
          const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top: -22 });
          return cell.original.name.length > EVENT_NAME_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.name}</p>
                </div>
              }
              position="top-start"
              {...moreProps}
            >
              <div className="ellipsis">{cell.original.name}</div>
            </PopOver>
          ) : (
            <div className="ellipsis">{cell.original.name}</div>
          );
        },
      },
      {
        Header: <TableHeader label="Description" identifier="description" />,
        accessor: 'description',
        sortable: false,
        filterable: false,
        maxWidth: 460,
        Cell: (cell: EventRow) => {
          const isNotFirstRow = cell.index !== 0;
          const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top: -38 });
          return cell.original.description.length > EVENT_DESCRIPTION_LENGTH ? (
            <PopOver
              interactive
              html={
                <div className="tooltip-info word-break">
                  <p className="tooltip-info__title">{cell.original.description}</p>
                </div>
              }
              position="top-start"
              {...moreProps}
            >
              <div className="ellipsis">{cell.original.description}</div>
            </PopOver>
          ) : (
            <div className="ellipsis">{cell.original.description}</div>
          );
        },
      },
      {
        Header: (
          <TableHeader
            label="Date"
            identifier="date"
            columnToSortBy={columnToSortBy}
            isAscendingOrdered={isAscendingOrdered}
            onClick={() => {
              if (columnToSortBy !== 'date') {
                setIsAscendingOrdered(true);
              } else {
                setIsAscendingOrdered(!isAscendingOrdered);
              }
              setColumnToSortBy('date');
            }}
          />
        ),
        accessor: 'date',
        maxWidth: 120,
      },
    ],
    [columnToSortBy, isAscendingOrdered, pageData, pageSize]
  );

  const getLinkCell = () => {
    return {
      Header: <TableHeader label="Link" identifier="link" />,
      accessor: 'link',
      sortable: false,
      filterable: false,
      maxWidth: 200,
      Cell: (cell: EventRow) => {
        const isNotFirstRow = cell.index !== 0;
        const linkExceedsLimit = cell.original.link.length > TOOLTIP_LIMIT_LENGTH;
        const top = linkExceedsLimit ? -40 : -20;
        const moreProps = isNotFirstRow ? {} : createPopperOptions({ zIndex: 10, top });
        return cell.original.link !== '-' ? (
          <PopOver
            interactive
            html={
              <div className="tooltip-info word-break">
                <p className="tooltip-info__title">{cell.original.link}</p>
              </div>
            }
            position="top-start"
            visible={Boolean(withoutProtocolAndSubDomainURL(cell.original.link).length)}
            {...moreProps}
          >
            <a
              href={cell.original.link}
              target="_blank"
              rel="noopener noreferrer"
              className="d-flex align-items-center table-link"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              <div className="table-link__text ellipsis">{withoutProtocolAndSubDomainURL(cell.original.link)}</div>
              <div className="center">
                <FiExternalLink className="table-link__icon" size={12} />
              </div>
            </a>
          </PopOver>
        ) : (
          <>{cell.original.link}</>
        );
      },
    };
  };

  const getDeleteCell = () => {
    const canEdit = Boolean(userPermissions[permissions.EVENTS.UPDATE]);
    const canDelete = Boolean(userPermissions[permissions.EVENTS.DELETE]);

    const canEditOrDelete = canEdit || canDelete;

    return {
      Header: '',
      width: 6,
      className: 'margin-left-auto',
      Cell: (row: EventRow) =>
        canEditOrDelete ? (
          <span
            className="verticle-ellipse cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <PopOver
              interactive
              open={openPopoverId === row.original.id}
              onRequestClose={() => setOpenPopoverId(null)}
              theme="light"
              size="regular"
              customClass="overflow-menu__regular"
              position="left-start"
              disableOnScroll={true}
              popperOptions={{
                modifiers: {
                  addZIndex: {
                    enabled: true,
                    order: 810,
                    fn: (data: PopperData) => ({
                      ...data,
                      styles: {
                        ...data.styles,
                        zIndex: 10,
                      },
                    }),
                  },
                },
              }}
              html={
                <EditDelete
                  onClose={() => setOpenPopoverId(null)}
                  onDelete={() => handleDelete(row)}
                  onEdit={() => handleEdit(row)}
                  canEdit={canEdit}
                  canDelete={canDelete}
                  type="event"
                  title={row.original.name}
                />
              }
            >
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenPopoverId(openPopoverId === row.original.id ? null : row.original.id);
                }}
              >
                <BsThreeDotsVertical size={20} />
              </span>
            </PopOver>
          </span>
        ) : null,
    };
  };

  const updateUrl = useCallback(
    (value: EventQueryState) => {
      const queryParam = stringify('', value, { arrayFormat: 'comma', encode: false });
      history.push({
        pathname: props.location.pathname,
        search: queryParam,
      });
    },
    [props.location.pathname]
  );

  useEffect(() => {
    updateUrl({
      ...queries,
    });
  }, [referencePeriod, page, size, props.location.search, queries, updateUrl]);

  const onPageChange = (selectedPage: number) => {
    setQueries((value) => ({ ...value, page: selectedPage }));
  };

  const onPageSizeChange = (selectedSize: number) => {
    setQueries((value) => ({ ...value, page: DEFAULT_PAGE_NUMBER, size: selectedSize }));
  };

  const handleApplyFilter = (filters: Pick<EventQueryState, 'referencePeriod'>) => {
    setQueries({ ...filters, page: DEFAULT_PAGE_NUMBER, size: DEFAULT_PAGE_SIZE });
  };

  return (
    <div className="full-scope-card">
      <div className="full-scope-card__header table-header name-wrap">
        <div>
          <div className="table-title">Events</div>
          <div className="table-subtitle">
            Showing {eventList.length} {pluralize('Result', eventList.length)}
          </div>
        </div>
        <div className="table-header__right">
          <PillTab
            tabs={EVENTS_REFERENCE_PERIOD_PILL_TABS}
            onTabClick={(value) => handleApplyFilter({ referencePeriod: String(value) })}
            isActive={(value) => String(value) === queries.referencePeriod.toLowerCase()}
          />
        </div>
      </div>
      <div className={classNames('full-scope-card__content')}>
        <>
          <Table
            loading={isDataLoading}
            data={eventList}
            columns={[...columns, getLinkCell(), getDeleteCell()]}
            showPagination={false}
            noDataText="No events to show."
            getTrProps={getRowProps}
          />
          <Pagination
            pageData={pageData}
            pageCount={+(pageCount || MINIMUM_PAGE_COUNT)}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </>
      </div>
    </div>
  );
};

export default EventList;
